import type { ReactNode } from "react";

/** Every illustrated icon of the set. */
export type SpotName =
  | "guides"
  | "productivite"
  | "actualites"
  | "temoignages"
  | "tasks"
  | "agenda"
  | "day"
  | "cap"
  | "notes"
  | "documents"
  | "whiteboards"
  | "projects"
  | "live"
  | "workload"
  | "calendars"
  | "import"
  | "mcp"
  | "team";

/** Colour family of a tile: the icon ink and the tinted ground it sits on. */
export type SpotTone = "blue" | "purple" | "navy" | "green" | "sky" | "pink" | "amber";

/**
 * Classes of each tone, for callers that colour more than the tile: `ink` (illustrations,
 * icons), `text` (the same hue made safe for small text: ≥ 4.5:1 on white and on
 * `ground`), `ground` (tinted background), `solid` (filled background), `badge` (a fill
 * that carries white text at ≥ 4.5:1) and `bar` (border colour).
 */
export const SPOT_TONES: Record<
  SpotTone,
  { ink: string; text: string; ground: string; solid: string; badge: string; bar: string }
> = {
  blue: { ink: "text-brand", text: "text-brand-ink", ground: "bg-blue-ulight", solid: "bg-blue-main", badge: "bg-blue-fill", bar: "border-blue-main" },
  purple: { ink: "text-funpurple", text: "text-funpurple", ground: "bg-funpurple-ter", solid: "bg-funpurple", badge: "bg-funpurple", bar: "border-funpurple" },
  navy: { ink: "text-funblue", text: "text-funblue", ground: "bg-funblue-ter", solid: "bg-funblue", badge: "bg-funblue", bar: "border-funblue" },
  green: { ink: "text-green-main", text: "text-green-ink", ground: "bg-green-ulight", solid: "bg-green-main", badge: "bg-green-ink", bar: "border-green-main" },
  sky: { ink: "text-sky-main", text: "text-sky-ink", ground: "bg-sky-ulight", solid: "bg-sky-main", badge: "bg-sky-ink", bar: "border-sky-main" },
  pink: { ink: "text-pink-main", text: "text-pink-ink", ground: "bg-pink-ulight", solid: "bg-pink-main", badge: "bg-pink-ink", bar: "border-pink-main" },
  amber: { ink: "text-amber-main", text: "text-amber-main", ground: "bg-amber-ulight", solid: "bg-amber-main", badge: "bg-amber-main", bar: "border-amber-main" },
};

// Shared paint: white paper with an ink outline, solid ink, and a soft ink tint.
const paper = { className: "fill-white", stroke: "currentColor", strokeWidth: 2.4, strokeLinejoin: "round" as const };
const line = { fill: "none", stroke: "currentColor", strokeWidth: 2.4, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
const ink = { fill: "currentColor" };
const tint = { fill: "currentColor", fillOpacity: 0.32 };
const whiteCheck = { fill: "none", strokeWidth: 2.2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, className: "stroke-white" };

// Drawn on a 48 × 48 grid: paper shapes first, ink details on top.
const ART: Record<SpotName, ReactNode> = {
  guides: (
    <>
      <path {...paper} d="M5 13c6-2.2 12-2 19 2v24c-7-4-13-4.2-19-2z" />
      <path {...paper} d="M43 13c-6-2.2-12-2-19 2v24c7-4 13-4.2 19-2z" />
      <path {...line} strokeOpacity={0.45} d="M10 20h9M10 26h7" />
      <path {...ink} d="M30 12.6V25l3.2-2.4 3.2 2.4V12.2z" />
      <path {...line} d="M29 31l2.6 2.6L37 28.2" />
    </>
  ),
  productivite: (
    <>
      <rect {...paper} x="6" y="7" width="26" height="33" rx="6" />
      <rect {...ink} x="11" y="12.5" width="7" height="7" rx="2" />
      <path {...whiteCheck} d="M12.8 16l1.5 1.5 2.6-2.8" />
      <path {...line} d="M22 16h5" />
      <rect {...line} x="11" y="24" width="7" height="7" rx="2" />
      <path {...line} strokeOpacity={0.45} d="M22 27.5h4" />
      <path {...ink} className="stroke-white" strokeWidth={2.4} strokeLinejoin="round" paintOrder="stroke" d="M37 17l-9.5 14h6.5l-2 11 10-15h-6.5z" />
    </>
  ),
  actualites: (
    <>
      <path {...paper} d="M13 29l2.5 10h5L18.5 29" />
      <path {...paper} d="M9 19h8l13-7.5v25L17 29H9a3 3 0 0 1-3-3v-4a3 3 0 0 1 3-3z" />
      <rect {...ink} x="28" y="10" width="5" height="28" rx="2.5" />
      <path {...line} d="M38 17l4-3M39 24h4.5M38 31l4 3" />
    </>
  ),
  temoignages: (
    <>
      <path {...tint} d="M27 6h9a6 6 0 0 1 6 6v7a6 6 0 0 1-6 6v5.5L30.5 25H32v-3a6 6 0 0 0-6-6h-6v-3a7 7 0 0 1 7-7z" />
      <path {...paper} d="M12 16h14a6 6 0 0 1 6 6v7a6 6 0 0 1-6 6h-7l-7 6v-6a6 6 0 0 1-6-6v-7a6 6 0 0 1 6-6z" />
      <path {...ink} d="M19 30.5s-5.5-3.2-5.5-6.6a2.8 2.8 0 0 1 5.5-1 2.8 2.8 0 0 1 5.5 1c0 3.4-5.5 6.6-5.5 6.6z" />
    </>
  ),
  tasks: (
    <>
      <rect {...paper} x="6" y="8" width="36" height="32" rx="7" />
      <circle {...ink} cx="15" cy="18.5" r="4.2" />
      <path {...whiteCheck} d="M13.2 18.6l1.3 1.3 2.4-2.6" />
      <path {...line} strokeOpacity={0.45} d="M23 18.5h12" />
      <circle {...line} cx="15" cy="29.5" r="4.2" />
      <path {...line} d="M23 29.5h9" />
    </>
  ),
  agenda: (
    <>
      <rect {...paper} x="6" y="9" width="36" height="32" rx="7" />
      <path {...ink} d="M6 16a7 7 0 0 1 7-7h22a7 7 0 0 1 7 7v2H6z" />
      <path {...line} d="M16 6v6M32 6v6" />
      <rect {...ink} x="11" y="23" width="14" height="5.5" rx="2.2" />
      <rect {...tint} fillOpacity={0.45} x="19" y="31" width="17" height="5.5" rx="2.2" />
    </>
  ),
  day: (
    <>
      <circle {...ink} cx="35" cy="12" r="5" />
      <path {...line} d="M35 3v1.5M44 12h-1.5M41.4 5.6l-1 1M28.6 5.6l1 1" />
      <rect {...paper} x="5" y="17" width="32" height="24" rx="6" />
      <rect {...ink} x="10" y="23" width="14" height="5" rx="2" />
      <rect {...tint} fillOpacity={0.45} x="15" y="31" width="17" height="5" rx="2" />
    </>
  ),
  cap: (
    <>
      <path {...paper} d="M12 7h24a6 6 0 0 1 6 6v14a6 6 0 0 1-6 6H22l-8 7v-7h-2a6 6 0 0 1-6-6V13a6 6 0 0 1 6-6z" />
      <path {...ink} d="M24 11.5c1 5.4 3.1 7.5 8.5 8.5-5.4 1-7.5 3.1-8.5 8.5-1-5.4-3.1-7.5-8.5-8.5 5.4-1 7.5-3.1 8.5-8.5z" />
      <path {...ink} fillOpacity={0.5} d="M34 24c.4 2 1 2.6 3 3-2 .4-2.6 1-3 3-.4-2-1-2.6-3-3 2-.4 2.6-1 3-3z" />
    </>
  ),
  notes: (
    <>
      <path {...paper} d="M12 5h17l10 10v25a3 3 0 0 1-3 3H12a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3z" />
      <path {...paper} d="M29 5v8a2 2 0 0 0 2 2h8" />
      <path {...line} strokeWidth={3.2} d="M15 21h11" />
      <path {...line} strokeOpacity={0.45} d="M15 28h17M15 34h11" />
    </>
  ),
  documents: (
    <>
      <path {...tint} d="M6 14a4 4 0 0 1 4-4h9l4 4h15a4 4 0 0 1 4 4v18a4 4 0 0 1-4 4H10a4 4 0 0 1-4-4z" />
      <rect {...paper} x="14" y="9" width="20" height="20" rx="3" />
      <path {...line} strokeOpacity={0.45} d="M19 15h10M19 20h7" />
      <path {...paper} d="M6 23h36v13a4 4 0 0 1-4 4H10a4 4 0 0 1-4-4z" />
      <rect {...ink} x="19" y="29" width="10" height="4" rx="2" />
    </>
  ),
  whiteboards: (
    <>
      <path {...line} d="M16 34l-4 8M32 34l4 8" />
      <rect {...paper} x="5" y="7" width="38" height="27" rx="5" />
      <circle {...line} cx="15" cy="20" r="5" />
      <path {...line} d="M21 20h5" />
      <path {...ink} d="M25.5 17.2L29 20l-3.5 2.8z" />
      <rect {...tint} fillOpacity={0.5} x="30" y="14" width="8" height="12" rx="2" />
    </>
  ),
  projects: (
    <>
      <rect {...tint} x="12" y="6" width="30" height="22" rx="6" />
      <rect {...paper} x="6" y="15" width="32" height="26" rx="6" />
      <path {...line} strokeWidth={3.2} d="M12 23h12" />
      <circle {...ink} className="stroke-white" strokeWidth={2} cx="14" cy="33" r="3.4" />
      <circle {...ink} fillOpacity={0.5} className="stroke-white" strokeWidth={2} cx="20" cy="33" r="3.4" />
      <path {...ink} d="M36 5.5l1.6 3.3 3.6.5-2.6 2.5.6 3.6-3.2-1.7-3.2 1.7.6-3.6-2.6-2.5 3.6-.5z" />
    </>
  ),
  live: (
    <>
      <rect {...paper} x="5" y="6" width="32" height="27" rx="5" />
      <path {...line} strokeOpacity={0.45} d="M10 13h17M10 19h12M10 25h8" />
      <path {...ink} className="stroke-white" strokeWidth={1.8} strokeLinejoin="round" d="M22 16v13l3.6-3.4 2.6 6 2.8-1.2-2.6-5.8h5z" />
      <path {...ink} fillOpacity={0.55} className="stroke-white" strokeWidth={1.8} strokeLinejoin="round" d="M31 27v12l3.3-3.1 2.4 5.5 2.6-1.1-2.4-5.4H41.5z" />
    </>
  ),
  workload: (
    <>
      <rect {...paper} x="5" y="7" width="38" height="34" rx="7" />
      <circle {...ink} cx="13" cy="16" r="3.2" />
      <rect {...ink} x="19" y="14" width="18" height="4" rx="2" />
      <circle {...ink} fillOpacity={0.5} cx="13" cy="24" r="3.2" />
      <rect {...tint} fillOpacity={0.5} x="19" y="22" width="10" height="4" rx="2" />
      <circle {...ink} cx="13" cy="32" r="3.2" />
      <rect {...ink} x="19" y="30" width="14" height="4" rx="2" />
    </>
  ),
  calendars: (
    <>
      <rect {...tint} x="14" y="5" width="29" height="27" rx="6" />
      <rect {...paper} x="5" y="14" width="29" height="28" rx="6" />
      <path {...ink} d="M5 20a6 6 0 0 1 6-6h17a6 6 0 0 1 6 6v1.5H5z" />
      <rect {...ink} x="10" y="27" width="11" height="4.5" rx="2" />
      <rect {...tint} fillOpacity={0.5} x="15" y="34" width="13" height="4.5" rx="2" />
    </>
  ),
  import: (
    <>
      <rect {...tint} x="29" y="5" width="13" height="11" rx="3" />
      <path {...paper} d="M6 27h9l3 5h12l3-5h9v9a5 5 0 0 1-5 5H11a5 5 0 0 1-5-5z" />
      <path {...line} strokeWidth={3} d="M24 6v17M17.5 16.5L24 23l6.5-6.5" />
    </>
  ),
  mcp: (
    <>
      <path {...line} d="M4.5 24h2.5M41 24h2.5" />
      <rect {...paper} x="7" y="17" width="14" height="14" rx="4" />
      <path {...line} d="M21 20.5h5M21 27.5h5" />
      <rect {...ink} x="28" y="15" width="13" height="18" rx="4.5" />
      <path {...ink} d="M24 5c.5 2.8 1.4 3.7 4.2 4.2-2.8.5-3.7 1.4-4.2 4.2-.5-2.8-1.4-3.7-4.2-4.2 2.8-.5 3.7-1.4 4.2-4.2z" />
    </>
  ),
  team: (
    <>
      <circle {...tint} fillOpacity={0.45} cx="11" cy="20" r="4.5" />
      <path {...tint} fillOpacity={0.45} d="M3 36a8 8 0 0 1 14-5.3" />
      <circle {...ink} cx="22" cy="16" r="6" />
      <path {...ink} d="M11 38a11 11 0 0 1 22 0z" />
      <circle {...paper} cx="37" cy="31" r="7" />
      <path {...line} d="M37 27.5v7M33.5 31h7" />
    </>
  ),
};

/**
 * A small illustrated icon from the Flots set: white paper shapes outlined and detailed in
 * the current text colour, drawn on a 48 × 48 grid. Decorative, so hidden from assistive tech.
 *
 * @param name - Which drawing to render.
 * @param className - Size and colour classes (the ink is `currentColor`).
 */
export function SpotIcon({ name, className = "h-8 w-8" }: { name: SpotName; className?: string }) {
  return (
    // The drawings keep a 2-unit margin on the 48 grid, so the view box crops it.
    <svg viewBox="2 2 44 44" aria-hidden="true" focusable="false" className={className}>
      {ART[name]}
    </svg>
  );
}

/**
 * A {@link SpotIcon} on its tinted rounded tile.
 *
 * @param name - Which drawing to render.
 * @param tone - Colour family of the tile.
 * @param onTint - The tile sits on a card already tinted with its tone: draw it white.
 * @param className - Tile size, radius and padding (defaults to a 48 px tile).
 */
export function SpotTile({
  name,
  tone,
  onTint = false,
  className = "h-12 w-12 rounded-2xl p-2",
}: {
  name: SpotName;
  tone: SpotTone;
  onTint?: boolean;
  className?: string;
}) {
  const { ink, ground } = SPOT_TONES[tone];
  return (
    <span className={`inline-flex shrink-0 items-center justify-center ${ink} ${onTint ? "bg-surface" : ground} ${className}`}>
      <SpotIcon name={name} className="h-full w-full" />
    </span>
  );
}
